/**
 * m3 echo Frontend - Model Comparison Tab
 *
 * Tools tab for comparing Layer 2 analysis variants: a model, a Gemini
 * thinking level (Standard = Google's default) and an SDK path ("Legacy" is
 * today's production path and only runs with the default level).
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  LinearProgress,
  TextField,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  modelComparisonApi,
  ComparisonRunResponse,
  ComparisonResultSummary,
  AvailableModelsResponse,
  ComparisonSdk,
} from '../../services/admin';
import ModelComparisonDetailDialog from './ModelComparisonDetailDialog';
import ModelComparisonMetrics from './ModelComparisonMetrics';
import { useModelCatalog } from '../../hooks/useModelCatalog';

const JOB_LIMIT_OPTIONS = [50, 100, 200] as const;
// Select value for "Standard" (Google's default thinking level).
const STANDARD = '';

interface ArmState {
  model: string;
  level: string; // STANDARD or a level
  sdk: ComparisonSdk;
}

const daysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

const sameArm = (a: ArmState, b: ArmState): boolean =>
  a.model === b.model && a.level === b.level && a.sdk === b.sdk;

export const ModelComparisonTab: React.FC = () => {
  const { modelLabel } = useModelCatalog();
  // State for available models
  const [availableModels, setAvailableModels] = useState<AvailableModelsResponse | null>(null);
  const [arm1, setArm1] = useState<ArmState>({ model: '', level: STANDARD, sdk: 'native' });
  const [arm2, setArm2] = useState<ArmState>({ model: '', level: STANDARD, sdk: 'native' });
  const [jobLimit, setJobLimit] = useState<number>(50);
  const [since, setSince] = useState<string>(daysAgo(7));
  const [sampleRunId, setSampleRunId] = useState<string>('');
  const [completedRuns, setCompletedRuns] = useState<ComparisonRunResponse[]>([]);

  // State for current run
  const [currentRun, setCurrentRun] = useState<ComparisonRunResponse | null>(null);
  const [results, setResults] = useState<ComparisonResultSummary[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [selectedResultId, setSelectedResultId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Polling interval ref
  const pollingRef = React.useRef<NodeJS.Timeout | null>(null);

  // Load available models on mount
  useEffect(() => {
    loadAvailableModels();
    loadStatus();
    loadCompletedRuns();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Start polling when run is in progress
  useEffect(() => {
    if (currentRun?.status === 'pending' || currentRun?.status === 'running') {
      pollingRef.current = setInterval(() => {
        loadStatus();
      }, 2000);
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [currentRun?.status]);

  const loadAvailableModels = async () => {
    try {
      const response = await modelComparisonApi.getAvailableModels();
      setAvailableModels(response.data);
      // Default arm 1: the current Layer 2 model (a fresh control arm)
      const models = response.data.available_models;
      if (models.length > 0) {
        const baseline = models.find((m) => m.model_code === response.data.baseline_model);
        setArm1((arm) => ({ ...arm, model: (baseline ?? models[0]).model_code }));
      }
    } catch (err: any) {
      console.error('Error loading available models:', err);
      setError('Fehler beim Laden der verfügbaren Modelle');
    }
  };

  const loadCompletedRuns = async () => {
    try {
      const response = await modelComparisonApi.getRuns(20);
      setCompletedRuns(response.data.filter((run) => run.status === 'completed'));
    } catch (err) {
      console.error('Error loading runs:', err);
    }
  };

  const levelsOf = (model: string): string[] =>
    availableModels?.available_models.find((m) => m.model_code === model)?.levels ?? [];

  /** Applies a change to an arm and keeps it valid: a level the model cannot
   * take falls back to Standard, and a concrete level switches Legacy to Native. */
  const updateArm = (setter: React.Dispatch<React.SetStateAction<ArmState>>, changes: Partial<ArmState>) => {
    setter((arm) => {
      const next = { ...arm, ...changes };
      if (next.level !== STANDARD && !levelsOf(next.model).includes(next.level)) next.level = STANDARD;
      if (next.level !== STANDARD && next.sdk === 'legacy') next.sdk = 'native';
      return next;
    });
  };

  const loadStatus = async () => {
    try {
      const response = await modelComparisonApi.getStatus();
      const run = response.data;
      setCurrentRun(run);

      // Load results if run is completed
      if (run && run.status === 'completed') {
        loadResults(run.id);
        loadCompletedRuns();
      }
    } catch (err: any) {
      console.error('Error loading status:', err);
    }
  };

  const loadResults = async (runId: number) => {
    setLoading(true);
    try {
      const response = await modelComparisonApi.getResults(runId);
      setResults(response.data.results);
      setCurrentRun(response.data.run);
    } catch (err: any) {
      console.error('Error loading results:', err);
      setError('Fehler beim Laden der Ergebnisse');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    if (!arm1.model) {
      setError('Bitte wählen Sie mindestens ein Vergleichsmodell aus');
      return;
    }

    setStarting(true);
    setError(null);

    try {
      const response = await modelComparisonApi.start({
        model1: arm1.model,
        model1_thinking_level: arm1.level || null,
        model1_sdk: arm1.sdk,
        model2: arm2.model || null,
        ...(arm2.model ? { model2_thinking_level: arm2.level || null, model2_sdk: arm2.sdk } : {}),
        job_limit: jobLimit,
        since: since || null,
        sample_run_id: sampleRunId ? Number(sampleRunId) : null,
      });
      setCurrentRun(response.data);
      setResults([]);
    } catch (err: any) {
      console.error('Error starting comparison:', err);
      setError(err.response?.data?.detail || 'Fehler beim Starten des Vergleichs');
    } finally {
      setStarting(false);
    }
  };

  const handleExport = async () => {
    if (!currentRun) return;

    try {
      const response = await modelComparisonApi.exportCSV(currentRun.id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `model_comparison_${currentRun.id}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Error exporting CSV:', err);
      setError('Fehler beim Exportieren');
    }
  };

  const handleRowClick = (resultId: number) => {
    setSelectedResultId(resultId);
    setDialogOpen(true);
  };

  const isRunning = currentRun?.status === 'pending' || currentRun?.status === 'running';
  const done = currentRun ? currentRun.processed_count + (currentRun.error_count ?? 0) : 0;
  const progress = currentRun && currentRun.total_results > 0 ? (done / currentRun.total_results) * 100 : 0;
  const identicalArms = Boolean(arm2.model) && sameArm(arm1, arm2);

  const renderArm = (
    title: string,
    arm: ArmState,
    setter: React.Dispatch<React.SetStateAction<ArmState>>,
    optional: boolean,
  ) => (
    <Grid item xs={12} md={6}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Modell</InputLabel>
          <Select
            value={arm.model}
            label="Modell"
            onChange={(e) => updateArm(setter, { model: e.target.value })}
            disabled={isRunning}
          >
            {optional && <MenuItem value="">Keins</MenuItem>}
            {availableModels?.available_models.map((m) => (
              <MenuItem key={m.model_code} value={m.model_code}>
                {m.display_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small" disabled={isRunning || !arm.model}>
          <InputLabel shrink>Thinking</InputLabel>
          <Select
            value={arm.level}
            label="Thinking"
            notched
            displayEmpty
            onChange={(e) => updateArm(setter, { level: e.target.value })}
          >
            <MenuItem value={STANDARD}>Standard</MenuItem>
            {levelsOf(arm.model).map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small" disabled={isRunning || !arm.model}>
          <InputLabel>SDK</InputLabel>
          <Select
            value={arm.sdk}
            label="SDK"
            onChange={(e) => updateArm(setter, { sdk: e.target.value as ComparisonSdk })}
          >
            <MenuItem value="native">Native</MenuItem>
            <MenuItem value="legacy" disabled={arm.level !== STANDARD}>
              Legacy (heutiger Produktionspfad)
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Grid>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Layer2 Vergleich</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadStatus}
            disabled={loading || isRunning}
          >
            Aktualisieren
          </Button>
          {currentRun?.status === 'completed' && results.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
            >
              CSV Export
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Model Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Vergleichsmodelle auswählen
        </Typography>
        {availableModels && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Aktuelles Layer-2-Modell: {modelLabel(availableModels.baseline_model)}. Für eine
            Entscheidung zwei frisch gerechnete Arme vergleichen, z. B. „Standard“ gegen „minimal“;
            der Abgleich mit den gespeicherten Analysen ist nur informativ.
          </Typography>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {renderArm('Arm 1 (erforderlich)', arm1, setArm1, false)}
          {renderArm('Arm 2 (optional)', arm2, setArm2, true)}
        </Grid>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small" disabled={isRunning || Boolean(sampleRunId)}>
              <InputLabel>Anzahl Antworten</InputLabel>
              <Select
                value={jobLimit}
                label="Anzahl Antworten"
                onChange={(e) => setJobLimit(Number(e.target.value))}
              >
                {JOB_LIMIT_OPTIONS.map((limit) => (
                  <MenuItem key={limit} value={limit}>
                    {limit}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Seit"
              type="date"
              size="small"
              fullWidth
              value={since}
              onChange={(e) => setSince(e.target.value)}
              disabled={isRunning || Boolean(sampleRunId)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small" disabled={isRunning}>
              <InputLabel>Stichprobe</InputLabel>
              <Select value={sampleRunId} label="Stichprobe" onChange={(e) => setSampleRunId(e.target.value)}>
                <MenuItem value="">Neue Stichprobe</MenuItem>
                {completedRuns.map((run) => (
                  <MenuItem key={run.id} value={String(run.id)}>
                    Wie Lauf #{run.id} ({run.total_results} Antworten)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              color="primary"
              startIcon={starting ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleStart}
              disabled={!arm1.model || isRunning || starting || identicalArms}
              fullWidth
            >
              {starting ? 'Wird gestartet...' : 'Vergleich starten'}
            </Button>
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {identicalArms
            ? 'Beide Arme sind identisch – bitte Modell, Thinking oder SDK unterscheiden.'
            : sampleRunId
              ? `Verwendet exakt die Antworten von Lauf #${sampleRunId} (auch die dort fehlgeschlagenen).`
              : `Bis zu ${jobLimit} gültige Antworten seit ${since || '–'}, abwechselnd über Projekte und Jobs verteilt.`}
        </Typography>
      </Paper>

      {/* Progress */}
      {isRunning && currentRun && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Vergleich läuft...
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress variant="determinate" value={progress} sx={{ '& .MuiLinearProgress-bar': { background: progress >= 100 ? 'linear-gradient(90deg, #4caf50, #81c784)' : 'linear-gradient(90deg, #678BA3, #8AA4B8)' } }} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {done} / {currentRun.total_results}
              {currentRun.error_count ? ` (${currentRun.error_count} fehlgeschlagen)` : ''}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Metrics */}
      {currentRun?.status === 'completed' && <ModelComparisonMetrics run={currentRun} />}

      {/* Results Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : results.length > 0 && currentRun ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Projekt</TableCell>
                <TableCell>Layer 1 Modell</TableCell>
                <TableCell>Suchbegriff</TableCell>
                <TableCell align="center">
                  Live
                </TableCell>
                <TableCell align="center">
                  {currentRun.variant_1}
                </TableCell>
                {currentRun.comparison_model_2 && (
                  <TableCell align="center">
                    {currentRun.variant_2}
                  </TableCell>
                )}
                {currentRun.comparison_model_2 && <TableCell align="center">1↔2</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((result) => (
                <TableRow
                  key={result.id}
                  hover
                  onClick={() => handleRowClick(result.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{result.job_result_id}</TableCell>
                  <TableCell>{result.project_name || '-'}</TableCell>
                  <TableCell>
                    <Chip label={result.layer1_model || '-'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{result.search_term}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {result.baseline_score}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pos: {result.baseline_position ?? '-'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {result.model1_score}
                        </Typography>
                        {result.model1_matches ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                          <CancelIcon fontSize="small" color="error" />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Pos: {result.model1_position ?? '-'}
                      </Typography>
                    </Box>
                  </TableCell>
                  {currentRun.comparison_model_2 && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {result.model2_score ?? '-'}
                          </Typography>
                          {result.model2_matches !== null && (
                            result.model2_matches ? (
                              <CheckCircleIcon fontSize="small" color="success" />
                            ) : (
                              <CancelIcon fontSize="small" color="error" />
                            )
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Pos: {result.model2_position ?? '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                  )}
                  {currentRun.comparison_model_2 && (
                    <TableCell align="center">
                      {result.model1_matches_model2 === null ? '–' : result.model1_matches_model2 ? (
                        <CheckCircleIcon fontSize="small" color="success" />
                      ) : (
                        <CancelIcon fontSize="small" color="error" />
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : currentRun?.status === 'completed' && results.length === 0 ? (
        <Alert severity="info">
          Keine Ergebnisse gefunden. Möglicherweise sind keine abgeschlossenen Jobs vorhanden.
        </Alert>
      ) : !isRunning && (
        <Alert severity="info">
          Wählen Sie Vergleichsmodelle aus und starten Sie einen Vergleich, um die Ergebnisse zu sehen.
        </Alert>
      )}

      {/* Detail Dialog */}
      <ModelComparisonDetailDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        resultId={selectedResultId}
        run={currentRun}
      />
    </Box>
  );
};

export default ModelComparisonTab;
